import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import moment from "moment";

import HeaderBread from "../../../../components/HeaderBread";
import { useDataLayerValue } from "../../../../DataLayer/DataLayer";
import Layout from "../../../../Layout";
import CustomHeaderTitle from "../../../../components/constants/CustomHeaderTitle";
import { getEntertainmentTransaction } from "../../../../utils/ApiCalls/BackOffice/Entertainment";

const statusChipStyle = (status) => {
  const base = {
    display: "inline-block",
    padding: "2px 10px",
    borderRadius: "12px",
    fontSize: "12px",
    fontWeight: 500,
    color: "#fff",
  };
  switch ((status || "").toLowerCase()) {
    case "confirmed":
      return { ...base, background: "#0ab402" };
    case "claimed":
      return { ...base, background: "#1565c0" };
    case "voided":
      return { ...base, background: "#616161" };
    case "failed":
      return { ...base, background: "#eb6363" };
    case "pending":
      return { ...base, background: "#f5a623" };
    default:
      return { ...base, background: "#9e9e9e" };
  }
};

const fieldStyle = {
  display: "flex",
  gap: "8px",
  marginBottom: "10px",
  alignItems: "flex-start",
};

const labelStyle = {
  fontWeight: 600,
  minWidth: "160px",
  color: "#555",
  fontSize: "13px",
};

const valueStyle = {
  fontSize: "13px",
  color: "#222",
};

const formatJson = (value) => {
  if (value == null) return "-";
  try {
    const parsed = typeof value === "string" ? JSON.parse(value) : value;
    return JSON.stringify(parsed, null, 2);
  } catch {
    return String(value);
  }
};

const preStyle = {
  background: "#f5f5f5",
  padding: "10px",
  borderRadius: "4px",
  fontSize: "12px",
  overflowX: "auto",
  overflowY: "auto",
  margin: 0,
  maxHeight: "300px",
};

const EntertainmentViewTransaction = () => {
  const { id } = useParams();
  const [, dispatch] = useDataLayerValue();
  const [transaction, setTransaction] = useState(null);

  const navigation = [
    { title: "Home", route: "/" },
    { title: "Report", route: "/shore/back-office/entertainment/report" },
    { title: "View", route: "#" },
  ];

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchData = async () => {
    dispatch({ type: "SET_LOADING", loading: true });
    const res = await getEntertainmentTransaction(id);
    if (res.error) {
      dispatch({
        type: "SET_ERROR_MESSAGE",
        errMess: { message: res.error, type: "error" },
      });
    } else {
      setTransaction(res.data || res);
    }
    dispatch({ type: "SET_LOADING", loading: false });
  };

  if (!transaction) return null;

  return (
    <Layout type={"read_per"} module_data={"Report"}>
      <CustomHeaderTitle title={"View Entertainment Transaction"} />
      <div>
        <HeaderBread navigation={navigation} title={"View Transaction"} />
        <section className="content" style={{ marginTop: "20px" }}>
          <div className="container-fluid">
            <div className="card" style={{ marginBottom: "20px" }}>
              <div className="card-header">
                <h5 style={{ margin: 0 }}>Transaction Summary</h5>
              </div>
              <div className="card-body">
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "4px 20px",
                  }}
                >
                  <div style={fieldStyle}>
                    <span style={labelStyle}>Transaction ID:</span>
                    <span style={valueStyle}>{transaction.transactionId || "-"}</span>
                  </div>
                  <div style={fieldStyle}>
                    <span style={labelStyle}>Reference Code:</span>
                    <span style={valueStyle}>{transaction.referenceCode || "-"}</span>
                  </div>
                  <div style={fieldStyle}>
                    <span style={labelStyle}>Cabin Number:</span>
                    <span style={valueStyle}>{transaction.cabinNumber || "-"}</span>
                  </div>
                  <div style={fieldStyle}>
                    <span style={labelStyle}>Guest Name:</span>
                    <span style={valueStyle}>{transaction.guestName || "-"}</span>
                  </div>
                  <div style={fieldStyle}>
                    <span style={labelStyle}>Res Acc ID:</span>
                    <span style={valueStyle}>{transaction.resAccId ?? "-"}</span>
                  </div>
                  <div style={fieldStyle}>
                    <span style={labelStyle}>RVC Number / Name:</span>
                    <span style={valueStyle}>
                      {transaction.rvcNumber || "-"} - {transaction.rvcName?.english || "-"}
                    </span>
                  </div>
                  <div style={fieldStyle}>
                    <span style={labelStyle}>Item Number / Name:</span>
                    <span style={valueStyle}>
                      {transaction.itemNumber ?? "-"} - {transaction.itemName?.english || "-"}
                    </span>
                  </div>
                  <div style={fieldStyle}>
                    <span style={labelStyle}>Itemizer Code:</span>
                    <span style={valueStyle}>{transaction.itemizerCode || "-"}</span>
                  </div>
                  <div style={fieldStyle}>
                    <span style={labelStyle}>Unit Price:</span>
                    <span style={valueStyle}>
                      {transaction.currency || ""}{" "}
                      {transaction.unitPrice != null ? Number(transaction.unitPrice).toFixed(2) : "-"}
                    </span>
                  </div>
                  <div style={fieldStyle}>
                    <span style={labelStyle}>Quantity:</span>
                    <span style={valueStyle}>{transaction.quantity ?? "-"}</span>
                  </div>
                  <div style={fieldStyle}>
                    <span style={labelStyle}>Total:</span>
                    <span style={valueStyle}>
                      {transaction.currency || ""}{" "}
                      {transaction.total != null ? Number(transaction.total).toFixed(2) : "-"}
                    </span>
                  </div>
                  <div style={fieldStyle}>
                    <span style={labelStyle}>Fidelio Business Date:</span>
                    <span style={valueStyle}>
                      {transaction.fidelioBusinessDate
                        ? moment(transaction.fidelioBusinessDate).format("YYYY-MM-DD")
                        : "-"}
                    </span>
                  </div>
                  <div style={fieldStyle}>
                    <span style={labelStyle}>Transaction At:</span>
                    <span style={valueStyle}>
                      {transaction.transactionDate
                        ? moment(transaction.transactionDate).format("DD-MM-YYYY HH:mm:ss")
                        : "-"}
                    </span>
                  </div>
                  <div style={fieldStyle}>
                    <span style={labelStyle}>Fidelio Posting ID:</span>
                    <span style={valueStyle}>{transaction.fidelioPostingId || "-"}</span>
                  </div>
                  <div style={fieldStyle}>
                    <span style={labelStyle}>Status:</span>
                    <span>
                      <span style={statusChipStyle(transaction.voided ? "Voided" : transaction.status)}>
                        {(transaction.voided ? "Voided" : transaction.status) || "-"}
                      </span>
                    </span>
                  </div>
                  {transaction.errorMessage && (
                    <div style={fieldStyle}>
                      <span style={labelStyle}>Error Message:</span>
                      <span style={{ ...valueStyle, color: "#eb6363" }}>{transaction.errorMessage}</span>
                    </div>
                  )}
                  {transaction.voidReason && (
                    <div
                      style={{
                        ...fieldStyle,
                        gridColumn: "1 / -1",
                        borderLeft: "3px solid var(--red, #d7182a)",
                        paddingLeft: "10px",
                      }}
                    >
                      <span style={labelStyle}>Void Reason:</span>
                      <span style={valueStyle}>{transaction.voidReason}</span>
                    </div>
                  )}
                  <div style={fieldStyle}>
                    <span style={labelStyle}>Created At:</span>
                    <span style={valueStyle}>
                      {transaction.createdAt
                        ? moment(transaction.createdAt).format("DD-MM-YYYY HH:mm:ss")
                        : "-"}
                    </span>
                  </div>
                  <div style={fieldStyle}>
                    <span style={labelStyle}>Updated At:</span>
                    <span style={valueStyle}>
                      {transaction.updatedAt
                        ? moment(transaction.updatedAt).format("DD-MM-YYYY HH:mm:ss")
                        : "-"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-header">
                <h5 style={{ margin: 0 }}>Fidelio Request / Response</h5>
              </div>
              <div className="card-body">
                <div style={{ marginBottom: "16px" }}>
                  <div style={{ ...labelStyle, marginBottom: "6px" }}>Request</div>
                  <pre style={preStyle}>{formatJson(transaction.request)}</pre>
                </div>
                <div>
                  <div style={{ ...labelStyle, marginBottom: "6px" }}>Response</div>
                  <pre style={preStyle}>{formatJson(transaction.response)}</pre>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default EntertainmentViewTransaction;
